import { useEffect, useMemo, useState } from "react";
import { useConfigure, useHits, useInstantSearch, useSearchBox } from "react-instantsearch";

import { ALGOLIA_CONFIG } from "@repo/shopify/algolia/config";
import { toAlgoliaClickTarget, withAlgoliaClick } from "@repo/shopify/algolia/insights";
import { markAlgoliaDown } from "@repo/shopify/algolia/status";
import type { IAlgoliaProductHit } from "@repo/shopify/types/api/algolia";
import type { IPredictiveSearchProduct } from "@repo/shopify/types/api/search";

import { toProductGroups } from "@hooks/algolia/groupHits";

/** 드롭다운에 보여 줄 상품 수. 기존 Shopify 예측검색의 limit 과 같다. */
const PREDICTIVE_LIMIT = 10;

/**
 * 받아 오는 변형 레코드 수. 인덱스가 변형 단위라 같은 상품이 사이즈·색상 수만큼 오므로,
 * 품절 상품을 뒤로 보낸 뒤에도 상품 10개를 채울 만큼 넉넉히 받는다.
 */
const PREDICTIVE_HITS = 200;

/** 드롭다운 카드(이미지 · 제목 · 링크)와 품절 판단에 필요한 필드만 받는다. */
const PREDICTIVE_ATTRIBUTES = [
  "objectID",
  "id",
  "title",
  "handle",
  "vendor",
  "product_image",
  "image",
  "inventory_available",
  "position",
  "meta",
];

/** Algolia 레코드에는 이미지 크기가 없어 thisisneverthat 상품 이미지 기준(2000×2667, 3:4)을 쓴다. */
const IMAGE_SIZE = { width: 2000, height: 2667 };

/** 같은 상품의 변형들 → 헤더 검색이 읽는 predictive product 모양 */
function toPredictiveProduct(variants: IAlgoliaProductHit[]): IPredictiveSearchProduct {
  const first = variants[0];

  return {
    id: `gid://shopify/Product/${String(first.id ?? first.objectID)}`,
    handle: first.handle,
    title: first.title,
    vendor: first.vendor,
    availableForSale: variants.some((variant) => variant.inventory_available ?? true),
    // 기존 예측검색의 featuredImage 는 상품 대표 이미지라 product_image 를 먼저 쓴다.
    featuredImage: { url: first.product_image ?? first.image ?? "", ...IMAGE_SIZE },
  } as IPredictiveSearchProduct;
}

/**
 * 헤더 검색 드롭다운용 Algolia 예측검색.
 *
 * 입력값으로 refine 하면 변형 레코드가 오고, 상품 단위로 묶어 기존 Shopify 예측검색과 같은 모양으로 돌려준다.
 * 요청이 실패하면 세션을 Shopify 쿼리로 되돌린다.
 */
export default function useAlgoliaPredictive() {
  const { refine } = useSearchBox();
  const { items } = useHits();
  const { status, error } = useInstantSearch();

  /** 빈 값은 Algolia 에 조회하지 않아 useSearchBox 의 query 가 직전 검색어로 남는다. 그래서 입력값을 따로 들고 있는다 */
  const [keyword, setKeyword] = useState("");

  useConfigure({
    hitsPerPage: PREDICTIVE_HITS,
    // 검색 결과 페이지와 같은 노출 조건(Gift 태그 · GWP 벤더 제외). hidden 은 toProductGroups 가 거른다.
    filters: ALGOLIA_CONFIG.visibleProductsFilter,
    attributesToRetrieve: PREDICTIVE_ATTRIBUTES,
    attributesToHighlight: [],
    attributesToSnippet: [],
  });

  useEffect(() => {
    if (error) markAlgoliaDown(error.message);
  }, [error]);

  const products = useMemo(() => {
    const groups = toProductGroups(items).map((variants) => ({ variants, product: toPredictiveProduct(variants) }));

    // 기존 예측검색의 unavailableProducts: "LAST" 와 같게 품절 상품을 뒤로 보낸다(순서는 유지).
    const ordered = [
      ...groups.filter(({ product }) => product.availableForSale),
      ...groups.filter(({ product }) => !product.availableForSale),
    ].slice(0, PREDICTIVE_LIMIT);

    // 클릭 이벤트용 queryID · 화면 순서를 실어 둔다(HeaderSearch 가 onClick 에서 보낸다).
    return ordered.map(({ variants, product }, index) => withAlgoliaClick(product, toAlgoliaClickTarget(variants[0], index + 1)));
  }, [items]);

  return {
    search: (value: string) => {
      setKeyword(value);
      if (!value) return;
      refine(value);
    },
    products: keyword ? products : [],
    loading: keyword !== "" && (status === "loading" || status === "stalled"),
  };
}
